import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Plus, Edit, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { WorkTypeFormDialog } from "@/components/WorkTypes/WorkTypeFormDialog";

export default function WorkTypes() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingWorkType, setEditingWorkType] = useState<any>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: workTypes, isLoading } = useQuery({
    queryKey: ["work_types"],
    queryFn: async () => {
      const { data, error } = await api
        .from("work_types")
        .select("*")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api
        .from("work_types")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work_types"] });
      toast({
        title: "Éxito",
        description: "Tipo de trabajo eliminado correctamente",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleEdit = (workType: any) => {
    setEditingWorkType(workType);
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm("¿Está seguro de eliminar este tipo de trabajo?")) {
      deleteMutation.mutate(id);
    }
  };

  const getMoldCaptureLabel = (value: number) => {
    switch (value) {
      case 0: return "No capturar";
      case 1: return "Opcional";
      case 2: return "Obligatorio";
      default: return "N/A";
    }
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto py-8 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Tipos de Trabajo</h1>
            <p className="text-muted-foreground">
              Gestiona los tipos de trabajo y sus configuraciones
            </p>
          </div>
          <Button onClick={() => {
            setEditingWorkType(null);
            setIsDialogOpen(true);
          }}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Tipo
          </Button>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Captura Molde</TableHead>
                <TableHead>Tipo Elemento</TableHead>
                <TableHead>Requiere Máquina</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : workTypes?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    No hay tipos de trabajo registrados
                  </TableCell>
                </TableRow>
              ) : (
                workTypes?.map((workType) => (
                  <TableRow key={workType.id}>
                    <TableCell className="font-medium">{workType.name}</TableCell>
                    <TableCell>
                      <Badge variant={workType.mold_capture === 2 ? "default" : "secondary"}>
                        {getMoldCaptureLabel(workType.mold_capture)}
                      </Badge>
                    </TableCell>
                    <TableCell>{workType.element_type}</TableCell>
                    <TableCell>
                      {workType.requires_machine ? "Sí" : "No"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={workType.is_active ? "default" : "secondary"}>
                        {workType.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(workType)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(workType.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <WorkTypeFormDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          workType={editingWorkType}
        />
      </div>
    </DashboardLayout>
  );
}
